import React, { useState, useEffect, useContext, memo, useMemo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { ConnectedProps, connect } from "react-redux";
import { extendedField } from "us.collection/actions";
import {
  EditOutlined,
  PlusOutlined,
  MoreOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "us.icons";
import Common from "us.common";
import { AddExtendedField } from "./Components";
import { ExtendedFieldType } from "us.collection/constants";
import { IDataModel } from "us.collection.case/reducers/Dashboard/Interfaces";
import { IMetaData } from "us.collection/interfaces";
import { ConfigurationContext } from "us.common/ConfigurationContext/ConfigurationProvider";
import { IOnFilter, IOnSort } from "us.common/components/antd/TableTree";
import {
  getEntityDetail,
  isSubCaseTransferredToCase,
} from "us.common/functions";
import { RootState } from "us.helper/types";
import { EntityType } from "./Constants/constants";
import { handleGoBack } from "us.helper/utility";
import { useLocation, useNavigate } from "react-router-dom";

const {
  $Tooltip,
  $Button,
  $Popover,
  $PageHeader,
  $Divider,
  $Drawer,
  $Affix,
  $Popconfirm,
  $Skeleton,
  $TableTree,
  $DateLabel,
} = Common.Components;

interface IExtendedFieldHome  {
  onMinimize(): void;
  getExtendedField: any;
  extendedFields: any;
  currentDateFormat: string;
  manageExtendedFieldDrawer?: any;
  drawer?: any;
  metaData: IDataModel<IMetaData>;
}

export interface ISortOrder {
  columnKey: string;
  order: any;
}

const ExtendedFieldHome: React.FC<IExtendedFieldHome & PropsFromRedux> = memo(
  (props) => {
    const { t } = useTranslation();
    const context = useContext(ConfigurationContext);
    const extendedFieldPermission =
      context.componentPermission["case"]?.routes["extendedField"];
    const navigate = useNavigate();
    const location = useLocation();
    const [popVisible, setPopVisibile] = useState<number>(-1);

    const {
      extendedFields,
      getExtendedField,
      manageExtendedFieldDrawer,
      drawer,
      metaData,
      // history,
      // location,
      deleteAdditionalInformation,
    } = props;

    const { caseNo, caseId, arId } = metaData.data ?? {};

    const { entityType, entityId } = useMemo(() => {
      return getEntityDetail(location.pathname, metaData.data);
    }, [caseId]);

    useEffect(() => {
      if (entityId && entityType) {
        fetchAdditionalInformations();
      }
    }, [metaData]);

    /**
     * @Description Delete information
     */
    const handleDelete = (recordId: any) => {
      setPopVisibile(-1);
      deleteAdditionalInformation &&
        deleteAdditionalInformation({
          entityId: entityType == ExtendedFieldType.AR ? arId : caseId,
          request: {
            category:
              entityType == EntityType.SUBCASE
                ? ExtendedFieldType.CASE
                : entityType,
            itemId: recordId,
          },
        });
    };

    // Popover actions in table
    const content = (record: any) => (
      <div className="table-more-content">
        <div
          className="d-flex flex-row more-item"
          onClick={() => {
            setPopVisibile(-1);
            handleEditExtended(record);
          }}
        >
          <div className="p-1">
            <EditOutlined />
          </div>
          <div className="p-1">
            {t("US.COLLECTION.COMMON:EXTENDED_FIELD.EDIT")}
          </div>
        </div>
        <$Popconfirm
          title={t("US.COLLECTION.COMMON:EXTENDED_FIELD.DELETECONFIRM")}
          onConfirm={() => handleDelete(record?.itemId)}
          onCancel={() => setPopVisibile(-1)}
        >
          <div className="d-flex mb-2 flex-row more-item text-error">
            <div className="p-1">
              <DeleteOutlined />
            </div>
            <div className="p-1">
              {t("US.COLLECTION.COMMON:EXTENDED_FIELD.DELETE")}
            </div>
          </div>
        </$Popconfirm>
      </div>
    );

    const columns: any = [
      {
        title: "",
        key: "more",
        dataIndex: "more",
        width: "50px",
        customRenderChild: (text: any, record: any) => {
          return (
            <$Popover
              placement="rightTop"
              content={content(record)}
              destroyTooltipOnHide
              trigger="click"
              open={record.itemId == popVisible}
              onOpenChange={(visbile: boolean) =>
                setPopVisibile(visbile ? record.itemId : -1)
              }
            >
              <$Button
                icon={<MoreOutlined />}
                size="small"
                disabled={
                  metaData.data && isSubCaseTransferredToCase(metaData.data)
                }
              />
            </$Popover>
          );
        },
      },
      {
        title: t("US.COLLECTION.COMMON:EXTENDED_FIELD.FIELD_NAME"),
        dataIndex: "fieldName",
        key: "fieldName",
        className: "text-nowrap",
        customSorter: (a: any, b: any) => a.localeCompare(b),
        customFilter: true,
      },
      {
        title: t("US.COLLECTION.COMMON:EXTENDED_FIELD.VALUE"),
        dataIndex: "value",
        key: "value",
        className: "text-wrap",
        customSorter: (a: any, b: any) => a.localeCompare(b),
        customFilter: true,
        render: (text: any, record: any) => {
          return (
            <>
              {record.dataType == "Date" && <$DateLabel value={record.value} />}
              {record.dataType != "Date" && record.value}
            </>
          );
        },
      },
      {
        title: t("US.COLLECTION.COMMON:EXTENDED_FIELD.MODIFIED_USER"),
        dataIndex: "modifiedUser",
        key: "modifiedUser",
        customSorter: (a: any, b: any) => a.localeCompare(b),
        customFilter: true,
        ellipsis: { showTitle: false },
        customRenderChild: (text: any) => (
          <$Tooltip placement="topLeft" title={text} key={text}>
            {text}
          </$Tooltip>
        ),
      },
      {
        title: t("US.COLLECTION.COMMON:EXTENDED_FIELD.MODIFIED_DATE"),
        dataIndex: "modifiedDate",
        key: "modifiedDate",
        width: "300px",
        customSorter: (a: any, b: any) => a.localeCompare(b),
        customFilter: "date",
        customRenderChild: (text: any) => {
          return (
            <span>
              <$DateLabel value={text} />
            </span>
          );
        },
      },
    ];

    const handleAddNewExtended = () => {
      manageExtendedFieldDrawer({
        title: t(
          "US.COLLECTION.COMMON:EXTENDED_FIELD.NEW_ADDITIONAL_INFORMATION"
        ),
        visible: true,
        isNewExtendedField: true,
        infoId: -1,
        fieldName: "",
      });
    };

    const handleEditExtended = (record: any) => {
      const { itemId, fieldName } = record;
      manageExtendedFieldDrawer({
        title: t(
          "US.COLLECTION.COMMON:EXTENDED_FIELD.EDIT_ADDITIONAL_INFORMATION"
        ),
        visible: true,
        isNewExtendedField: false,
        infoId: itemId,
        fieldName: fieldName,
      });
    };

    const handleClose = () => {
      manageExtendedFieldDrawer({
        title: "",
        visible: false,
        isNewExtendedField: true,
        infoId: -1,
        fieldName: "",
      });
    };

    const onClose = () => {
      manageExtendedFieldDrawer({
        title: "",
        visible: false,
        isNewExtendedField: true,
        infoId: -1,
        fieldName: "",
      });
    };

    const handleSort: IOnSort = (sortData, dataSource) => {
      return sortData(dataSource);
    };

    const handleFilter: IOnFilter = (searchData, dataSource) => {
      return searchData(dataSource);
    };

    const fetchAdditionalInformations = () => {
      getExtendedField({
        category:
          entityType == EntityType.SUBCASE
            ? ExtendedFieldType.CASE
            : entityType,
        entityId: entityType == ExtendedFieldType.AR ? arId : caseId,
      });
    };

    return (
      <Formik enableReinitialize initialValues={{}} onSubmit={() => {}}>
        {() => (
          <>
            <div className="space-content">
              <$Affix offsetTop={80}>
                <div className="page-header header-border">
                  <div className="d-flex flex-row align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <$PageHeader
                        className="px-0"
                        title={t(
                          "US.COLLECTION.COMMON:EXTENDED_FIELD.ADDITIONAL_INFORMATION"
                        )}
                        onBack={() => handleGoBack(navigate)}
                      />
                      <$Divider className="bui-devider" type="vertical" />
                      {extendedFieldPermission?.components["newField"]
                        ?.isEnabled && (
                        <$Skeleton
                          loading={extendedFields?.isFetching}
                          active
                          paragraph={{
                            rows: 0,
                          }}
                        >
                          <$Button
                            type="dashed"
                            size="small"
                            onClick={() => handleAddNewExtended()}
                            icon={<PlusOutlined />}
                            disabled={
                              metaData.data &&
                              isSubCaseTransferredToCase(metaData.data)
                            }
                          >
                            {t(
                              "US.COLLECTION.COMMON:EXTENDED_FIELD.ADD_NEW_FIELD"
                            )}
                          </$Button>
                        </$Skeleton>
                      )}
                    </div>
                    <div className="d-flex align-items-center mr-3 ml-5">
                      <$Tooltip
                        title={t("US.COLLECTION.COMMON:COMMON.REFRESH")}
                      >
                        <$Button
                          type="dashed"
                          size="small"
                          onClick={() => fetchAdditionalInformations()}
                          icon={<ReloadOutlined />}
                          className="ml-3"
                        />
                      </$Tooltip>
                    </div>
                  </div>
                </div>
              </$Affix>

              <$Skeleton
                loading={extendedFields?.isFetching}
                active
                paragraph={{ rows: 2 }}
              >
                <$TableTree
                  rowKey="itemId"
                  data={extendedFields?.data ?? []}
                  size="small"
                  className="mt-3"
                  onSort={handleSort}
                  onFilter={handleFilter}
                  filterOnType={true}
                  resetOnSourceChange={true}
                  bordered
                  pagination={{
                    defaultPageSize: 15,
                  }}
                  scroll={{
                    x: 1200,
                  }}
                  columns={columns}
                  onRow={(record: any) => {
                    return {
                      onDoubleClick: () => {
                        handleEditExtended(record);
                      },
                    };
                  }}
                />
              </$Skeleton>
            </div>

            <$Drawer
              title={drawer.title}
              width={"800px"}
              open={drawer.visible}
              onClose={() => onClose()}
              destroyOnClose={true}
              maskClosable={false}
            >
              <AddExtendedField onCancel={handleClose} />
            </$Drawer>
          </>
        )}
      </Formik>
    );
  }
);

const mapStateToProps = (state: RootState) => {
  const { common, extendedField, domainView } = state;
  const { currentDateFormat } = common;
  const { extendedFields, drawer } = extendedField;
  const { metaData } = domainView;
  return {
    extendedFields,
    drawer,
    currentDateFormat,
    metaData,
  };
};
const { deleteExtendedField } = extendedField;

const mapDispatchToProps = {
  getExtendedField: extendedField.extendedFields.get,
  manageExtendedFieldDrawer: extendedField.drawer.set,
  deleteAdditionalInformation: deleteExtendedField.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(ExtendedFieldHome);
